import React, { useState, useEffect } from "react";
import { recordService, listInventory, fetchAdminProfile } from "../../api/api";
import styles from "./StringerServiceForm.module.css";

const StringerServiceForm = () => {
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [strings, setStrings] = useState([]);
  const [loadingStrings, setLoadingStrings] = useState(true);
  const [stringsError, setStringsError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [stringerProfile, setStringerProfile] = useState(null);
  const [formData, setFormData] = useState({
    string_id: "",
    service_type: "offline",
    stringing_type: "normal",
    customer_string: "no",
    custom_string_name: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    receipt_no: "",
    racket_name: "",
    racket_info: "",
    tension_info: "",
    gripping_done: "no",
    grip_type: "",
    total_payment: "",
    remarks: "",
    booking_id: "",
    performed_by: "",
    service_date: new Date().toISOString().split("T")[0],
    racket_from_marina: "no",
    serviced_at_location: "",
  });

  useEffect(() => {
    fetchStrings();
    fetchStringerProfile();
  }, []);

  const fetchStrings = async () => {
    try {
      setLoadingStrings(true);
      setStringsError(null);
      const response = await listInventory();

      // Ensure response.data is an array
      if (!response.data || !Array.isArray(response.data)) {
        console.error("Invalid response format:", response);
        throw new Error("Invalid response format from server");
      }

      const availableStrings = response.data.filter(
        (item) =>
          item.string_name !== "Bring Own String" && item.remaining_quantity > 0
      );
      setStrings(availableStrings);
    } catch (error) {
      console.error("Error fetching strings:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load string inventory. Please try again or contact support.";
      setStringsError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoadingStrings(false);
    }
  };

  const fetchStringerProfile = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      const response = await fetchAdminProfile(token);
      const profile = response.data;
      setStringerProfile(profile);

      // Auto-fill the performed_by field with stringer's name
      setFormData((prev) => ({
        ...prev,
        performed_by: `${profile.first_name} ${profile.last_name}`.trim(),
      }));
    } catch (error) {
      console.error("Error fetching stringer profile:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.customer_string === "no" && !formData.string_id) {
      alert("Please select a string");
      return;
    }

    if (formData.customer_string === "yes" && !formData.custom_string_name) {
      alert("Please enter the customer's string name");
      return;
    }

    try {
      setLoading(true);

      const selectedString = strings.find(
        (s) => s.string_id === formData.string_id
      );

      const serviceData = {
        ...formData,
        booking_id: formData.booking_id || null,
        string_name:
          formData.customer_string === "yes"
            ? formData.custom_string_name
            : selectedString?.string_name || null,
        performed_by_name: formData.performed_by,
      };

      await recordService(serviceData);

      console.log(`[StringerServiceForm] Service recorded — customer: "${serviceData.customer_name}", racket: "${serviceData.racket_name}", phone: ${serviceData.customer_phone || 'N/A'}`);
      setSuccessMessage("Service recorded successfully!");

      setFormData({
        string_id: "",
        service_type: "offline",
        stringing_type: "normal",
        customer_string: "no",
        custom_string_name: "",
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        receipt_no: "",
        racket_name: "",
        racket_info: "",
        tension_info: "",
        gripping_done: "no",
        grip_type: "",
        total_payment: "",
        remarks: "",
        booking_id: "",
        performed_by: "",
        service_date: new Date().toISOString().split("T")[0],
        racket_from_marina: "no",
        serviced_at_location: "",
      });

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } catch (error) {
      console.error("Error recording service:", error);
      alert(error.response?.data?.message || "Failed to record service");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h1 className={styles.headerTitle}>Record Stringing Service</h1>
      </div>

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}

      {showForm && (
        <div className={styles.formCard}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Service Date *</label>
                <input
                  type="date"
                  name="service_date"
                  value={formData.service_date}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Service Type *</label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="offline">Offline/Walk-in</option>
                  <option value="online">Online Booking</option>
                  <option value="walk-in">In-Store</option>
                </select>
              </div>

              {/* Stringing Type */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Stringing Type *</label>
                <select
                  name="stringing_type"
                  value={formData.stringing_type}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="normal">Normal</option>
                  <option value="express">Express</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Customer's String? *</label>
                <select
                  name="customer_string"
                  value={formData.customer_string}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {formData.customer_string === "yes" ? (
                <div className={styles.colSpanTwo}>
                  <label className={styles.formLabel}>String Name *</label>
                  <input
                    type="text"
                    name="custom_string_name"
                    value={formData.custom_string_name}
                    onChange={handleChange}
                    placeholder="Enter customer's string name"
                    required
                    className={styles.formInput}
                  />
                </div>
              ) : (
                <div className={styles.colSpanTwo}>
                  <label className={styles.formLabel}>String Used *</label>
                  <select
                    name="string_id"
                    value={formData.string_id}
                    onChange={handleChange}
                    required
                    disabled={loadingStrings}
                    className={styles.formSelect}
                  >
                    <option value="">
                      {loadingStrings
                        ? "Loading strings..."
                        : "Select a string"}
                    </option>
                    {strings.map((string) => (
                      <option key={string.string_id} value={string.string_id}>
                        {string.string_name}{" "}
                        {string.brand ? `(${string.brand})` : ""} - Stock:{" "}
                        {string.remaining_quantity}
                      </option>
                    ))}
                  </select>
                  {strings.length === 0 && !loadingStrings && (
                    <div style={{ marginTop: "8px" }}>
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                        }}
                      >
                        No strings available in stock. Please update inventory.
                      </small>
                      {stringsError && (
                        <button
                          type="button"
                          onClick={fetchStrings}
                          style={{
                            marginTop: "8px",
                            padding: "6px 12px",
                            fontSize: "14px",
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          Retry Loading Inventory
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Racket Name</label>
                <input
                  type="text"
                  name="racket_name"
                  value={formData.racket_name}
                  onChange={handleChange}
                  placeholder="e.g., Babolat Pure Drive"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>String Tension</label>
                <input
                  type="text"
                  name="tension_info"
                  value={formData.tension_info}
                  onChange={handleChange}
                  placeholder="e.g., 55lbs"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Gripping Done? *</label>
                <select
                  name="gripping_done"
                  value={formData.gripping_done}
                  onChange={handleChange}
                  required
                  className={styles.formSelect}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {formData.gripping_done === "yes" && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Grip Type *</label>
                  <input
                    type="text"
                    name="grip_type"
                    value={formData.grip_type}
                    onChange={handleChange}
                    placeholder="e.g., Overgrip, Replacement Grip"
                    required
                    className={styles.formInput}
                  />
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Total Payment (AED) *
                </label>
                <input
                  type="number"
                  name="total_payment"
                  value={formData.total_payment}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  required
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Customer Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Customer Phone</label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Customer Email</label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Receipt No. *</label>
                <input
                  type="text"
                  name="receipt_no"
                  value={formData.receipt_no}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Booking ID (if online)
                </label>
                <input
                  type="text"
                  name="booking_id"
                  value={formData.booking_id}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Performed By</label>
                <input
                  type="text"
                  name="performed_by"
                  value={formData.performed_by}
                  readOnly
                  className={`${styles.formInput} ${styles.readOnlyInput}`}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Location</label>
                <input
                  type="text"
                  value={stringerProfile?.location || "Not assigned"}
                  readOnly
                  className={`${styles.formInput} ${styles.readOnlyInput}`}
                />
              </div>

              {/* Marina Tracking */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Racket from Marina?{" "}
                  {stringerProfile?.location === "Marina Store" && "*"}
                </label>
                <select
                  name="racket_from_marina"
                  value={formData.racket_from_marina}
                  onChange={handleChange}
                  required={stringerProfile?.location === "Marina Store"}
                  className={styles.formSelect}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes (From Marina)</option>
                </select>
              </div>

              {formData.racket_from_marina === "yes" && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Serviced At *</label>
                  <select
                    name="serviced_at_location"
                    value={formData.serviced_at_location}
                    onChange={handleChange}
                    required
                    className={styles.formSelect}
                  >
                    <option value="">Select service location</option>
                    <option value="Dubai Hills Store">Dubai Hills Store</option>
                    <option value="Jumeirah Store">Jumeirah Store</option>
                  </select>
                </div>
              )}

              <div className={styles.colSpanTwo}>
                <label className={styles.formLabel}>Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows="3"
                  className={styles.formTextarea}
                  placeholder="Additional notes about the service..."
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className={`${styles.actionButton} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`${styles.actionButton} ${styles.buttonPrimary}`}
              >
                {loading ? "Recording..." : "Record Service"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default StringerServiceForm;
